import { useEffect, useMemo, useState } from "react";

type Options = {
  cacheKey: string;
  refresh?: boolean;
};

const getStoredImage = (key: string) => {
  if (typeof window === "undefined") return "";

  try {
    return window.localStorage.getItem(key) || "";
  } catch {
    return "";
  }
};

const setStoredImage = (key: string, value: string) => {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Ignore storage failures; the direct image URL still works.
  }
};

const withRefreshParam = (url: string) => {
  const joiner = url.includes("?") ? "&" : "?";
  return `${url}${joiner}v=${Date.now()}`;
};

export const useCachedImage = (url: string, { cacheKey, refresh = true }: Options) => {
  const cleanUrl = useMemo(() => String(url || "").trim(), [url]);
  const storageKey = useMemo(() => `cached-image:${cacheKey}:${cleanUrl}`, [cacheKey, cleanUrl]);
  const [src, setSrc] = useState(() => getStoredImage(storageKey) || cleanUrl);

  useEffect(() => {
    if (!cleanUrl) {
      setSrc("");
      return;
    }

    const cached = getStoredImage(storageKey);
    setSrc(cached || cleanUrl);

    if (!refresh || typeof window === "undefined") {
      if (!cached) setStoredImage(storageKey, cleanUrl);
      return;
    }

    const nextSrc = withRefreshParam(cleanUrl);
    const image = new Image();

    image.onload = () => {
      setStoredImage(storageKey, nextSrc);
      setSrc(nextSrc);
    };
    image.src = nextSrc;
  }, [cleanUrl, refresh, storageKey]);

  return src;
};
